import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import Drawer from '@mui/material/Drawer';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ArrowLeft, ShoppingBag, ShieldCheck, Minus, Plus, Trash2, Lock } from 'lucide-react';
import { useAppStore, useCheckoutStore } from '../../store/Store';
import CartItemSkeleton from '../CartItemSkeleton';

import axios from 'axios';
import { BASE_URL } from '../../apis/constant';

// CartItem Component - NO CHANGES NEEDED
const CartItem = ({
  item,
  onUpdateQuantity,
  onRemove,
}) => {
  const price = Number(item.price) || 0;
  const offerPrice = item.offerPrice ? Number(item.offerPrice) : price;
  const quantity = Number(item.quantity) || 1;
  const totalPrice = offerPrice * quantity;
  const hasDiscount = offerPrice < price;

  return (
    <div className="flex gap-4 bg-white p-3 border border-gray-100 rounded-2xl mb-4 shadow-sm transition-all duration-200">
      {/* Image */}
      <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50 border border-gray-50 self-center">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTMwQzE2NS44MTIgMTMwIDEzOCAxNTcuODEyIDEzOCAxOTJDMTM4IDIyNi4xODggMTY1LjgxMiAyNTQgMjAwIDI1NEMyMzQuMTg4IDI1NCAyNjIgMjI2LjE4OCAyNjIgMTkyQzI2MiAxNTcuODEyIDIzNC4xODggMTMwIDIwMCAxMzBaTTI1NiAyODRIMTQ0QzEzMS4yNjQgMjg0IDEyMCAyOTUuMjY0IDEyMCAzMDhWMzQwQzEyMCAzNDYuNjI4IDEyNS4zNzIgMzUyIDEzMiAzNTJIMjY4QzI3NC42MjggMzUyIDI4MCAzNDYuNjI4IDI4MCAzNDBWMzA4QzI4MCAyOTUuMjY0IDI2OC43MzYgMjg0IDI1NiAyODRaIiBmaWxsPSIjRDREOERCIi8+Cjwvc3ZnPgo=';
          }}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col flex-grow justify-between py-0.5 overflow-hidden">
        {/* Top Row: Name & Delete */}
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm truncate leading-tight mb-1">
              {item.name || 'Unnamed Product'}
            </h3>
            {item.variantType && item.variantValue && (
              <p className="text-xs text-gray-500 font-medium">
                {item.variantType}: <span className="text-gray-700">{item.variantValue}</span>
              </p>
            )}
          </div>

          <button
            onClick={() => onRemove(item.id)}
            className="text-gray-300 hover:text-red-500 transition-colors p-1 -mr-1"
            aria-label="Remove item"
          >
            <Trash2 size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Bottom Row: Quantity & Price Layout */}
        <div className="flex justify-between items-end mt-2">
          {/* Quantity Selector */}
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-8 shadow-sm">
            <button
              onClick={() => onUpdateQuantity(item.id, -1)}
              disabled={quantity <= 1}
              className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-l-lg transition-colors"
            >
              <Minus size={14} strokeWidth={2.5} />
            </button>
            <span className="w-6 text-center text-xs font-bold text-gray-900">
              {quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, 1)}
              disabled={quantity >= (item.stock || 10)}
              className="w-8 h-full flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-30 active:bg-gray-200 rounded-r-lg transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} />
            </button>
          </div>

          {/* Price Representation */}
          <div className="text-right flex flex-col items-end justify-end">
            <div className="text-[10px] font-medium text-gray-400 mb-0.5">
              {quantity} x ৳{offerPrice.toLocaleString()}
              {hasDiscount && (
                <span className="ml-1 line-through text-gray-300">
                  ৳{price.toLocaleString()}
                </span>
              )}
            </div>
            <p className="text-base font-extrabold text-gray-900 leading-none">
              ৳{totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CartDrawer = ({ open, onClose }) => {
  const {
    cartItems = [],
    cartCount = 0,
    updateCartItemQuantity,
    removeFromCart,
    initializeCart,
    cartApiLoading,
  } = useAppStore();

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const navigate = useNavigate();
  const { setCheckoutItems } = useCheckoutStore();
  const { closeDrawer } = useAppStore();

  // Initialize cart when component mounts
  useEffect(() => {
    initializeCart();
  }, [initializeCart]);

  // Calculate subtotal
  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      const price = Number(item.price) || 0;
      const offerPrice = item.offerPrice ? Number(item.offerPrice) : price;
      const quantity = Number(item.quantity) || 1;
      return acc + (offerPrice * quantity);
    }, 0);
  }, [cartItems]);

  const handleUpdateQuantity = (id, delta) => {
    const item = cartItems.find(item => item.id === id);
    if (item) {
      const currentQuantity = Number(item.quantity) || 1;
      const stock = Number(item.stock) || 10;
      const newQuantity = Math.max(1, Math.min(stock, currentQuantity + delta));
      updateCartItemQuantity(id, newQuantity);
    }
  };




  const RemoveFromCart = async (id) => {
    const customer_id = localStorage.getItem("customerId");
    if (!customer_id) return false;

    try {
      const { status } = await axios.post(`${BASE_URL}/api/remove_from_cart/`, {
        customer_id,
        cart_item_id: id
      });
      return status === 200;
    } catch (error) {
      console.error('Error:', error);
      return false;
    }
  };

  const handleRemoveItem = async (id) => {
    const success = await RemoveFromCart(id);
    if (success) removeFromCart(id);
  };



  function transformProducts(input) {
    return input.map((item, index) => {
      const imagePath = '/' + item.image.replace(/^https?:\/\/[^/]+\/?/, '');


      return {
        id: (index + 1).toString(),
        name: item.name,
        productId: item.productId,
        variantId: item.variantId,
        variant_type: item.variantType,
        variant: item.variantValue,
        price: item.offerPrice && item.offerPrice !== item.price ? item.offerPrice : item.price,
        originalPrice: item.price,
        quantity: item.quantity,
        image: imagePath
      };
    });
  }



  const handleCheckout = () => {
    setCheckoutItems(transformProducts(cartItems));
    closeDrawer();
    navigate(`/checkout`);
  };

  // Empty State
  const EmptyCart = () => (
    <div className="w-full h-full bg-white text-gray-900 flex flex-col items-center justify-center p-6">
      <div className="w-48 h-48 mb-4">
        <DotLottieReact
          src="https://lottie.host/6fa380fe-08d4-44f8-9d8c-cfc446905b3d/tYfL8dvHle.lottie"
          loop
          autoplay
        />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Items</h3>
      <p className="text-gray-400 text-sm mb-5">Start adding products</p>

      <button
        onClick={onClose}
        className="bg-black text-white text-sm font-medium py-2.5 px-7 rounded-lg hover:bg-gray-800 transition-all duration-200 hover:scale-105"
      >
        Browse Products
      </button>
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 450 },
          maxWidth: '450px',
          boxSizing: 'border-box'
        }
      }}
    >
      <div className="w-full h-full bg-white relative flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex-shrink-0 bg-white/90 backdrop-blur-md border-b border-gray-100 px-5 py-4 flex items-center justify-between z-20">
          <button
            onClick={onClose}
            className="p-2 -ml-2 text-gray-500 hover:text-black transition-colors rounded-full hover:bg-gray-50"
          >
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-black uppercase tracking-wide">
            Shopping Cart
          </h1>
          <div className="w-8 h-8 flex items-center justify-center relative">
            <ShoppingBag size={20} className="text-black" />
            {cartItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-black text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2 ring-white">
                {cartCount}
              </span>
            )}
          </div>
        </header>

        {/* Scrollable Content */}
        <main className="flex-grow overflow-y-auto p-5 pb-40">
          {cartApiLoading ? (
            <div className="space-y-2.5">
              {[...Array(4)].map((_, index) => (
                <CartItemSkeleton key={index} />
              ))}
            </div>
          ) : cartItems.length === 0 ? (
            <EmptyCart />
          ) : (
            <div className="space-y-2.5">
              {cartItems.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemoveItem}
                />
              ))}
            </div>
          )}
        </main>

        {/* Footer: Subtotal & Checkout - Only show when cart has items */}



        {cartItems.length > 0 && (
          <div className="flex-shrink-0 bg-white border-t border-gray-100 p-5 pb-6 z-20">
            {/* Compact Pricing */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Subtotal</span>
                <div className="text-xs text-gray-400 mt-0.5">{cartCount} items</div>
              </div>
              <span className="text-xl font-bold text-gray-900">
                ৳{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Compact Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={isCheckingOut}
              className="w-full bg-gray-900 hover:bg-black text-white font-semibold text-sm py-3.5 rounded-xl transition-all duration-200 disabled:opacity-70 flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-[0.98]"
            >
              {isCheckingOut ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Checkout Now
                </>
              )}
            </button>

            {/* Compact Security Footer */}
            <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-500">
              <ShieldCheck size={12} className="text-green-500" />
              <span>Secure & encrypted checkout</span>
            </div>
          </div>
        )}


      </div>
    </Drawer>
  );
};

export default CartDrawer;